'use client';

import React from 'react';
import { IconStarFilled } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { Avatar, Divider } from '@/components/ui';
import { useOwner } from '../../context/OwnerProvider';
import { RatingContainer } from '../info/RatingContainer';
import { titleCase } from '@repo/utils';

/**
 * Owner Sticky Card - Sticky sidebar card for owner info
 * Uses centered layout for sidebar display
 */
export function OwnerStickyCard() {
  const { owner } = useOwner();

  const { name, roles, numBuildings, numUnits, averageRating, reviewCount } = owner;

  const ownerName = name || 'Owner';
  const avatarLabel = ownerName.trim().charAt(0).toUpperCase();
  const primaryRole = roles && roles.length > 0 ? roles[0] : 'Property manager';
  const buildingCount = numBuildings || 0;
  const unitCount = numUnits || 0;

  const ownerDetails = [
    primaryRole,
    buildingCount > 0 && `${buildingCount} building${buildingCount === 1 ? '' : 's'}`,
    unitCount > 0 && `${unitCount} unit${unitCount === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <div
      className={cn(
        'z-[10] w-full',
        'bg-primary rounded-3xl border border-tertiary',
        'shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.06)]',
        'flex flex-col items-center pt-6 pb-3 px-4',
      )}
    >
      {/* Title Container - Centered layout for sticky card */}
      <div className={cn('flex flex-col items-center gap-2 px-2 pb-3 w-full')}>
        {/* Avatar */}
        <Avatar
          type="Owner"
          className="text-heading-5 shrink-0 !size-16"
          label={avatarLabel}
          size={64}
        />

        {/* Name */}
        <h2 className={cn('text-heading-5 text-primary text-center')}>{titleCase(ownerName)}</h2>

        {/* Owner Details */}
        <div
          className={cn(
            'flex flex-wrap items-center justify-center',
            'gap-1',
            'text-body-2 text-secondary text-center',
          )}
        >
          {ownerDetails.map((detail, index) => (
            <span key={index} className={cn('flex items-center gap-1')}>
              {index > 0 && <span>·</span>}
              <span className={cn('overflow-hidden text-ellipsis')}>{detail}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className={cn('w-full py-0.5')}>
        <Divider variant="tertiary" />
      </div>

      {/* Rating Container - Centered for sticky card */}
      <div className={cn('flex items-center gap-4 justify-center px-2 py-3 w-full')}>
        <RatingContainer averageRating={averageRating || 0} reviewCount={reviewCount || 0} />
      </div>
    </div>
  );
}
